import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';

interface SubUser {
  id: number;
  firstName: string;
  lastName?: string;
  email: string;
  mobile: string;
  roleId?: number | null;
  role?: { id: number; name: string } | null;
  isActive: boolean;
  createdAt: string;
}
interface Role { id: number; name: string; }

export function MerchantTeamTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SubUser | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: users, isLoading } = useQuery<SubUser[]>({
    queryKey: ['merchant', 'sub-users'],
    queryFn: async () => (await api.get<SubUser[]>('/merchant/sub-users')).data,
  });
  const { data: roles } = useQuery<Role[]>({
    queryKey: ['merchant', 'roles', 'short'],
    queryFn: async () => (await api.get<Role[]>('/merchant/roles')).data,
    staleTime: 30_000,
  });

  const save = useMutation({
    mutationFn: async (u: any) => {
      if (u.id) {
        const { id, password, ...rest } = u;
        const body: any = { ...rest };
        if (password) body.password = password;
        return (await api.patch(`/merchant/sub-users/${id}`, body)).data;
      }
      return (await api.post('/merchant/sub-users', u)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant', 'sub-users'] });
      toast.success('User saved');
      setShowForm(false);
      setEditing(null);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/merchant/sub-users/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant', 'sub-users'] }); toast.success('User deactivated'); },
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Team members</h3>
          <p className="text-sm text-slate-500">Add accounts/operations users and assign each a role.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-primary text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          <Plus size={16} /> Invite user
        </button>
      </div>

      {isLoading && <div className="p-8 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Mobile</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && users?.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400"><Users size={28} className="mx-auto opacity-40 mb-2" />No team members yet</td></tr>
            )}
            {users?.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-900">{u.firstName} {u.lastName ?? ''}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3 text-slate-600">{u.mobile}</td>
                <td className="px-4 py-3">
                  {u.role ? <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">{u.role.name}</span> : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => { setEditing(u); setShowForm(true); }} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"><Pencil size={14} /></button>
                    <button onClick={() => { if (confirm(`Deactivate ${u.email}?`)) del.mutate(u.id); }} className="p-1.5 text-danger hover:bg-danger/10 rounded"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <SubUserForm
          initial={editing}
          roles={roles ?? []}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={(d) => save.mutate({ ...d, ...(editing ? { id: editing.id } : {}) })}
          busy={save.isPending}
        />
      )}
    </div>
  );
}

function SubUserForm({ initial, roles, onSave, onCancel, busy }: {
  initial: SubUser | null; roles: Role[];
  onSave: (d: any) => void; onCancel: () => void; busy: boolean;
}) {
  const [firstName, setFirstName] = useState(initial?.firstName ?? '');
  const [lastName, setLastName] = useState(initial?.lastName ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [mobile, setMobile] = useState(initial?.mobile ?? '');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState<number | ''>(initial?.roleId ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-3">{initial ? 'Edit user' : 'Invite user'}</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs">First name</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
            </div>
            <div>
              <label className="text-xs">Last name</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs">Email</label>
            <input type="email" value={email} disabled={!!initial} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none disabled:bg-slate-50" />
          </div>
          <div>
            <label className="text-xs">Mobile</label>
            <input value={mobile} onChange={(e) => setMobile(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
          </div>
          <div>
            <label className="text-xs">Password {initial && <span className="text-slate-400">(leave blank to keep)</span>}</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
          </div>
          <div>
            <label className="text-xs">Role</label>
            <select value={roleId} onChange={(e) => setRoleId(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 border rounded-lg outline-none">
              <option value="">— no role —</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={() => onSave({ firstName, lastName, email, mobile, password, roleId: roleId || null, isActive })}
            disabled={busy || !email || (!initial && !password)}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg disabled:opacity-50">
            {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
